import { useState, useEffect } from 'react';
import { Chart } from 'primereact/chart';
import { ToggleButton } from 'primereact/togglebutton';

export const ChartPersonalizado = ({ datos, nombreCliente = 'Cliente', textoDatos, tooltipCallback, maximo, descripcionExtra, nivelesAlerta }) => {
    const [chartType, setChartType] = useState('line');
    const [chartData, setChartData] = useState({});
    const [chartOptions, setChartOptions] = useState({});

    const chartTypes = [
        { label: 'Línea', value: 'line' },
        { label: 'Barras', value: 'bar' },
        // { label: 'Torta', value: 'pie' },
        // { label: 'Dona', value: 'doughnut' },
        // { label: 'Polar', value: 'polarArea' },
        // { label: 'Radar', value: 'radar' }
    ];

    const hexToRgb = (hex) => {
        const cleanedHex = hex.replace('#', '');
        const bigint = parseInt(cleanedHex, 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;
        return [r, g, b];
    };

    const getColorGradient = (valor, maximo) => {
        const ratio = Math.min(valor / maximo, 1); // de 0 a 1

        // Verde (bajo) → Naranja (medio) → Rojo (alto)
        const interpolateColor = (start, middle, end, t) => {
            const lerp = (a, b, t) => a + (b - a) * t;

            if (t < 0.5) {
                // verde → naranja
                const tt = t * 2;
                return `rgb(${Math.round(lerp(start[0], middle[0], tt))},${Math.round(lerp(start[1], middle[1], tt))},${Math.round(lerp(start[2], middle[2], tt))})`;
            } else {
                // naranja → rojo
                const tt = (t - 0.5) * 2;
                return `rgb(${Math.round(lerp(middle[0], end[0], tt))},${Math.round(lerp(middle[1], end[1], tt))},${Math.round(lerp(middle[2], end[2], tt))})`;
            }
        };

        const bajo = hexToRgb(nivelesAlerta.find(n => n.sigla === 'NO_ALERTA')?.color_bg || '4CAF50'); // verde
        const medio = hexToRgb(nivelesAlerta.find(n => n.sigla === 'REVISION')?.color_bg || 'FFA500'); // naranja
        const alto = hexToRgb(nivelesAlerta.find(n => n.sigla === 'ALERTA')?.color_bg || 'FF0000'); // rojo

        return interpolateColor(bajo, medio, alto, ratio);
    };

    // const legendColor = getColorGradient(promedio, maximo);

    const createSegmentedGradient = (canvas, chartArea, valores, maxVal, getColorGradient) => {
        const gradient = canvas.createLinearGradient(chartArea.left, 0, chartArea.right, 0);
        const step = 1 / (valores.length - 1);

        for (let i = 0; i < valores.length - 1; i++) {
            const val1 = valores[i];
            const val2 = valores[i + 1];
            const offset1 = step * i;
            const offset2 = step * (i + 1);

            const ratio1 = Math.min(val1 / maxVal, 1);
            const ratio2 = Math.min(val2 / maxVal, 1);

            const color1 = getColorGradient(val1, maxVal);
            const color2 = getColorGradient(val2, maxVal);
            const colorMid = getColorGradient(maxVal * 0.5, maxVal); // nivel medio fijo

            const crossesMid = (ratio1 < 0.5 && ratio2 > 0.5) || (ratio1 > 0.5 && ratio2 < 0.5);

            if (crossesMid) {
                const offsetMid = offset1 + (offset2 - offset1) * ((0.5 - ratio1) / (ratio2 - ratio1));
                gradient.addColorStop(offset1, color1);
                gradient.addColorStop(offsetMid, colorMid);
                gradient.addColorStop(offset2, color2);
            } else {
                gradient.addColorStop(offset1, color1);
                gradient.addColorStop(offset2, color2);
            }
        }

        return gradient;
    };

    useEffect(() => {
        if (datos?.length > 0) {
            const fechas = datos.map(t => t.fecha);
            const valores = datos.map(t => parseFloat(t.valor));
            const maxVal = parseFloat(maximo) || 0;
            const colors = valores.map(valor => getColorGradient(valor, maxVal));
            const isLineChart = chartType === 'line';

            setChartData({
                labels: fechas,
                datasets: [
                    {
                        label: textoDatos,
                        data: valores,
                        backgroundColor: isLineChart ? 'rgba(0,0,0,0)' : maximo ? colors : 'rgba(33, 150, 243, 0.7)',
                        borderColor: maximo
                            ? (ctx) => {
                                const chart = ctx.chart;
                                const { ctx: canvas, chartArea } = chart;
                                if (!chartArea) return 'rgba(0,0,0,0)';
                                return createSegmentedGradient(canvas, chartArea, valores, parseFloat(maximo), getColorGradient);
                            }
                            : 'rgba(33, 150, 243, 0.7)',
                        pointBackgroundColor: isLineChart && maximo ? colors : 'rgba(44, 255, 44, 0.8)',
                        pointBorderColor: isLineChart && maximo ? colors : 'rgba(0, 122, 2, 1)',
                        fill: false,
                        tension: 0.4,
                        tooltipDatos: datos
                    }
                ]
            });

            setChartOptions({
                responsive: true,
                plugins: {
                    legend: {
                        position: 'top',
                    },
                    title: {
                        display: true,
                        text: `Gráfico de ${nombreCliente}`
                    },
                    tooltip: {
                        callbacks: {
                            label: tooltipCallback
                                ? (context) => tooltipCallback(context)
                                : (context) => {
                                    const item = context.dataset.tooltipDatos?.[context.dataIndex];
                                    if (!item || typeof item !== 'object') return '';
                                    const entries = Object.entries(item)
                                        .filter(([_, value]) => value !== null && value !== undefined)
                                        .map(([key, value]) => `${key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}: ${value}`);

                                    return entries.length > 0 ? entries : '';
                                }
                        }
                    },
                },
                scales: chartType === 'pie' || chartType === 'doughnut' || chartType === 'polarArea' ? {} : {
                    y: {
                        beginAtZero: true
                    }
                }
            });
        }
    }, [datos, chartType]);

    return (
        <div className="p-fluid">
            <div className="toggle-descripcion-container" style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem', }} >
                <ToggleButton checked={chartType === 'line'} onChange={(e) => setChartType(e.value ? 'line' : 'bar')} onLabel="Línea" offLabel="Barras" onIcon="pi pi-chart-line" offIcon="pi pi-chart-bar" className="p-button-outlined" />
                {descripcionExtra && (
                    <div style={{ fontStyle: 'italic', color: '#555', flex: 1, minWidth: '200px', }}>
                        {descripcionExtra}
                    </div>
                )}
            </div>
            <Chart type={chartType} data={chartData} options={chartOptions} />
        </div>
    );
};